import * as Yup from 'yup';
import { differenceInYears } from 'date-fns';
import { FarmRiskType, StructureCategory } from '@ourbranch/lookups';
import { getIn } from 'formik';
import { isRentersRiskType } from '@ourbranch/farm-utils';

import {
  validHomePurchaseDate,
  requiredForRisks,
  requiredIfFO6BarnsOutbuildings,
  requiredIfFO6SilosGrainBins,
  requiredIfFO6ConfinementBuildings,
  requiredIfFO6HoopBuildings,
  requiredIfFO6DwellingContents,
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled,
  requiredIfFO6DwellingContentsAndHeatingEnabled,
  requiredForTowers,
  requiredForGrainStorage,
  requiredForNonTowerSilos,
  requiredIfFO6SilosGrainBinsAndHeatingEnabled,
  requiredIfFO6PoultryAndHeatingEnabled,
  validYearBuilt,
  requiredWhenCanEditCoverageAmount,
  coverageInRangeRequiredWhenEditable,
  mobileHomeSqFtValidation,
  isDwellingOrRentersRisk,
  nullifyIfNaN,
  requiredForFO6Structures,
  rc80PercentRequirement,
  invalidMobileHomeConfigForFo3,
  requiresSwimmingPoolType
} from 'common/helpers/yup-helpers';
import { brandConstants, brandConfig } from 'brand-config';
import { getConstructionTypeExemptPropertyTypes } from 'common/helpers/farm-helpers';

/**
 * Helper function to get building class 1 maximum age from brand configuration
 * @param {string} state - State code (e.g., 'IL', 'MO')
 * @returns {number} Maximum age in years for Building Class 1 (defaults to 25)
 */
const getBuildingClass1MaxAge = (state = 'IL') => {
  return brandConfig?.farm?.[state]?.[FarmRiskType.Fo6Structures]?.businessRules?.buildingClass1MaxAgeYears ?? 25;
};

/**
 * Helper function to detect if a risk is new (not yet saved)
 * @param {object} formRiskData - The risk data from form context
 * @returns {boolean} True if the risk is new (has no id), false otherwise
 */
const isNewRisk = (formRiskData) => !formRiskData?.id;

Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);
Yup.addMethod(Yup.mixed, 'requiredForRisks', requiredForRisks);
Yup.addMethod(
  Yup.mixed,
  'requiredIfFO6BarnsOutbuildingsAndHeatingEnabled',
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled
);
Yup.addMethod(Yup.mixed, 'requiredForTowers', requiredForTowers);
Yup.addMethod(Yup.mixed, 'requiredForGrainStorage', requiredForGrainStorage);
Yup.addMethod(Yup.mixed, 'requiredForNonTowerSilos', requiredForNonTowerSilos);
Yup.addMethod(Yup.mixed, 'requiredIfFO6SilosGrainBinsAndHeatingEnabled', requiredIfFO6SilosGrainBinsAndHeatingEnabled);
Yup.addMethod(Yup.mixed, 'requiredIfFO6PoultryAndHeatingEnabled', requiredIfFO6PoultryAndHeatingEnabled);
Yup.addMethod(
  Yup.mixed,
  'requiredIfFO6DwellingContentsAndHeatingEnabled',
  requiredIfFO6DwellingContentsAndHeatingEnabled
);
Yup.addMethod(Yup.string, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6SilosGrainBins', requiredIfFO6SilosGrainBins);
Yup.addMethod(Yup.string, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6DwellingContents', requiredIfFO6DwellingContents);
Yup.addMethod(Yup.number, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6SilosGrainBins', requiredIfFO6SilosGrainBins);
Yup.addMethod(Yup.number, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6DwellingContents', requiredIfFO6DwellingContents);
Yup.addMethod(Yup.number, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.mixed, 'requiredWhenCanEditCoverageAmount', requiredWhenCanEditCoverageAmount);
Yup.addMethod(Yup.mixed, 'coverageInRangeRequiredWhenEditable', coverageInRangeRequiredWhenEditable);
Yup.addMethod(Yup.string, 'validYearBuilt', validYearBuilt);
Yup.addMethod(Yup.number, 'mobileHomeSqFtValidation', mobileHomeSqFtValidation);
Yup.addMethod(Yup.number, 'nullifyIfNaN', nullifyIfNaN);
Yup.addMethod(Yup.string, 'requiredForFO6Structures', requiredForFO6Structures);
Yup.addMethod(Yup.number, 'rc80PercentRequirement', rc80PercentRequirement);
Yup.addMethod(Yup.string, 'invalidMobileHomeConfigForFo3', invalidMobileHomeConfigForFo3);

// Add new Yup validation method for Type 1 barn requirements
export const barnType1Validation = function (message) {
  return this.test('barn-type-1-validation', message, function (value) {
    // Get risk type and building data from correct context
    // this.from[1].value should be the risk object when validating coverage fields
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const buildingType = getIn(formRiskData, 'building.buildingType');

    // Only validate for FO6 Barns/Outbuildings
    if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
      return true;
    }

    const buildingClass = getIn(formRiskData, 'building.buildingClass');

    // Check if this is a Type 1 barn based on user-selected building class
    const isType1Barn = buildingClass === 1;

    if (!isType1Barn) {
      return true; // Not Type 1, validation passes
    }

    // For Type 1 eligible barns, validate required fields
    const field = this.path.split('.').pop();

    if (field === 'structureLossSettlement' && value !== 'RC') {
      return this.createError({
        message: 'Type 1 barns must use Replacement Cost loss settlement'
      });
    }

    if (field === 'specialFormCoverage' && value !== true) {
      return this.createError({
        message: 'Type 1 barns must include Special Form coverage'
      });
    }

    if (field === 'hasSnowWeightCoverage' && value !== true) {
      return this.createError({
        message: 'Type 1 barns must include Weight of Ice, Snow, or Sleet coverage'
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.mixed, 'barnType1Validation', barnType1Validation);

/**
 * Coverage C Validation
 *
 * Validates Coverage C based on residence usage and brandConfig rules.
 * - FO1: Residence-usage-based rules (Issue #28614)
 * - FO2: Loss-settlement + residence-usage rules from brandConfig (Issue #29011)
 * - FO3: Residence-usage rules from brandConfig (Issue #29011)
 * - FO4: Minimum $10,000, no maximum (Issue #29011)
 */
export const validateDwellingCoverageC = function (message) {
  return this.test('fo1-coverage-c-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const residenceUsage = getIn(formRiskData, 'building.residenceUsage');
    const coverageA = getIn(formRiskData, 'coverages.coverageA');
    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;

    // FO1 validation
    if (riskType === FarmRiskType.Fo1) {
      const businessRules = brandConfig?.farm?.[state]?.[riskType]?.businessRules?.coverageC;
      if (!businessRules?.byResidenceUsage) {
        return true; // No business rules defined
      }

      const rules = businessRules.byResidenceUsage[residenceUsage];
      if (!rules) {
        return true; // No rules for this residence usage
      }

      const currentValue = Number(value) || 0;
      const maxValue = rules.max === 'coverageA' ? coverageA : rules.max;

      if (currentValue < rules.min) {
        return this.createError({
          message: `Coverage C cannot be less than $${rules.min.toLocaleString()}`
        });
      }

      if (currentValue > maxValue) {
        if (residenceUsage === 'R') {
          return this.createError({
            message: `Coverage C cannot exceed $${maxValue.toLocaleString()} for tenant occupied rental properties`
          });
        }
        return this.createError({
          message: `Coverage C cannot exceed $${maxValue.toLocaleString()}`
        });
      }

      return true;
    }

    // FO2 and FO3: brandConfig-driven Coverage C validation (Issue #29011)
    if ([FarmRiskType.Fo2, FarmRiskType.Fo3].includes(riskType)) {
      const lossSettlement = getIn(formRiskData, 'coverages.lossSettlement');
      const businessRules = brandConfig?.farm?.[state]?.[riskType]?.businessRules?.coverageC;

      // Resolve rules: FO2 uses byLossSettlement (ACV vs RC differ), FO3 uses byResidenceUsage
      let rules;
      if (businessRules?.byLossSettlement && lossSettlement) {
        rules = businessRules.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
      }
      // Fall through to byResidenceUsage as secondary lookup regardless of byLossSettlement presence
      if (!rules && businessRules?.byResidenceUsage && residenceUsage) {
        rules = businessRules.byResidenceUsage[residenceUsage];
      }

      if (rules) {
        const minValue = rules.minPctOfCoverageA ? coverageA * rules.minPctOfCoverageA : (rules.min ?? 0);
        const maxValue = rules.max === 'coverageA' ? coverageA : rules.max;

        if (value < minValue) {
          const minLabel = rules.minPctOfCoverageA
            ? `$${minValue.toLocaleString()} (${rules.minPctOfCoverageA * 100}% of Coverage A)`
            : `$${minValue.toLocaleString()}`;
          return this.createError({
            message: `Coverage C must be at least ${minLabel}`
          });
        }

        if (value > maxValue) {
          return this.createError({
            message: `Coverage C cannot exceed $${maxValue.toLocaleString()}`
          });
        }

        return true;
      }

      return true;
    }

    // FO4: Minimum $10,000, no maximum (Issue #29011)
    if (riskType === FarmRiskType.Fo4) {
      if (value != null && value < 10000) {
        return this.createError({
          message: 'Coverage C must be at least $10,000'
        });
      }

      return true;
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateDwellingCoverageC', validateDwellingCoverageC);

/**
 * FO1 Coverage D Validation
 *
 * Validates Coverage D based on residence usage for FO1 risks.
 * - Primary Residence: $0 - $3,000
 * - Seasonal: $0 - $3,000
 * - Tenant Occupied Rental: $0 - $3,000
 * - Vacant: $0 only (not editable)
 *
 * Related issue: #28614
 */
export const validateFO1CoverageD = function (message) {
  return this.test('fo1-coverage-d-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const residenceUsage = getIn(formRiskData, 'building.residenceUsage');
    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;

    // Only validate for FO1
    if (riskType !== FarmRiskType.Fo1) {
      return true;
    }

    // Get business rules from brand config
    const businessRules = brandConfig?.farm?.[state]?.[riskType]?.businessRules?.coverageD;
    if (!businessRules?.byResidenceUsage) {
      return true; // No business rules defined
    }

    const rules = businessRules.byResidenceUsage[residenceUsage];
    if (!rules) {
      return true; // No rules for this residence usage
    }

    const currentValue = Number(value) || 0;

    if (currentValue < rules.min) {
      return this.createError({
        message: `Coverage D cannot be less than $${rules.min.toLocaleString()}`
      });
    }

    if (currentValue > rules.max) {
      if (residenceUsage === 'V') {
        return this.createError({
          message: 'Coverage D is not available for vacant properties'
        });
      }
      return this.createError({
        message: `Coverage D cannot exceed $${rules.max.toLocaleString()}`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateFO1CoverageD', validateFO1CoverageD);

/**
 * Wind/Hail Deductible Validation
 *
 * Validates that Wind/Hail deductible is at least as high as the All-Peril deductible.
 * Wind/Hail deductible is always stored as a decimal percentage (0.01 = 1%, 0.015 = 1.5%, 0.02 = 2%)
 * and must be converted to a dollar amount using coverageA before comparison.
 *
 * Related issue: #28860
 */
export const validateWindHailDeductible = function (message) {
  return this.test('wind-hail-deductible-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');

    // Only validate for FO1, FO2, FO3, FO4 (dwelling risks)
    if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType)) {
      return true;
    }

    const allPerilDeductible = getIn(formRiskData, 'coverages.deductibleAllOther');

    // If there's no All-Peril deductible set, skip validation
    if (!allPerilDeductible) {
      return true;
    }

    // Skip validation if Wind/Hail deductible is not set (field is nullable)
    // Check before converting to Number to properly handle null/undefined
    if (value === null || value === undefined) {
      return true;
    }

    const windHailValue = Number(value);
    const allPerilValue = Number(allPerilDeductible);

    // Get Coverage A to calculate dollar amounts for percentage deductibles
    // Wind/Hail can be either percentage (< 1) or dollar amount (>= 1)
    // Get the reference coverage to calculate dollar amounts for percentage deductibles
    // Percentage deductibles are represented as values < 1 (e.g., 0.01 = 1%)
    // FO-4 (renters) has no Coverage A — use Coverage C instead
    const referenceCoverage = isRentersRiskType(riskType)
      ? getIn(formRiskData, 'coverages.coverageC')
      : getIn(formRiskData, 'coverages.coverageA');

    // Calculate actual dollar amounts
    // If value < 1, it's a percentage deductible, multiply by the reference coverage
    const windHailDollarAmount =
      windHailValue < 1 ? (referenceCoverage ? windHailValue * referenceCoverage : null) : windHailValue;

    const allPerilDollarAmount =
      allPerilValue < 1 ? (referenceCoverage ? allPerilValue * referenceCoverage : null) : allPerilValue;

    // If wind/hail is a percentage but coverage is missing
    if (windHailValue < 1 && windHailDollarAmount === null) {
      // For renters risks (FO4), skip validation when coverage is missing
      // For dwelling risks, reject to enforce coverage requirement
      if (isRentersRiskType(riskType)) {
        return true;
      }
      return this.createError({
        message: 'Coverage amount is required to calculate percentage-based Wind/Hail deductible'
      });
    }

    // If all-peril is a percentage but coverage is missing
    if (allPerilValue < 1 && allPerilDollarAmount === null) {
      // For renters risks (FO4), skip validation when coverage is missing
      // For dwelling risks, reject to enforce coverage requirement
      if (isRentersRiskType(riskType)) {
        return true;
      }
      return this.createError({
        message: 'Coverage amount is required to calculate percentage-based All-Peril deductible'
      });
    }

    // Round both sides to handle floating point precision issues
    const windHailRounded = Math.round(windHailDollarAmount);
    const allPerilRounded = Math.round(allPerilDollarAmount);

    if (windHailRounded < allPerilRounded) {
      // Format display values for error message
      const windHailDisplay =
        windHailValue < 1
          ? `${(windHailValue * 100).toFixed(0)}% ($${windHailRounded.toLocaleString()})`
          : `$${windHailDollarAmount.toLocaleString()}`;

      const allPerilDisplay =
        allPerilValue < 1
          ? `${(allPerilValue * 100).toFixed(0)}% ($${allPerilRounded.toLocaleString()})`
          : `$${allPerilValue.toLocaleString()}`;

      return this.createError({
        message: `Wind/Hail deductible (${windHailDisplay}) cannot be less than All-Peril deductible (${allPerilDisplay})`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateWindHailDeductible', validateWindHailDeductible);

/**
 * FO6 Wind/Hail Deductible Validation
 *
 * Validates that Wind/Hail deductible is at least as high as the All-Peril deductible for FO6 risks.
 * Handles different coverage types:
 * - FO6Structures: Uses coverageE
 * - FO6Scheduled: Uses coverageF
 * - FO6Unscheduled: Uses coverageG (read-only, but validate for completeness)
 *
 * Wind/Hail deductible can be either a decimal percentage (< 1, e.g., 0.01 = 1%) or a dollar amount (>= 1)
 * and must be converted to a dollar amount using the appropriate coverage before comparison.
 *
 * Related issue: #28860
 */
export const validateFO6WindHailDeductible = function (message) {
  return this.test('fo6-wind-hail-deductible-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');

    // Only validate for FO6 risks
    if (![FarmRiskType.Fo6Structures, FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled].includes(riskType)) {
      return true;
    }

    const allPerilDeductible = getIn(formRiskData, 'coverages.deductibleAllOther');

    // If there's no All-Peril deductible set, skip validation
    if (!allPerilDeductible) {
      return true;
    }

    // Skip validation if Wind/Hail deductible is not set (field is nullable)
    // Check before converting to Number to properly handle null/undefined
    if (value === null || value === undefined) {
      return true;
    }

    const windHailValue = Number(value);
    const allPerilValue = Number(allPerilDeductible);

    // Determine which coverage to use based on FO6 risk type
    let coverage = 0;
    let coverageLabel = '';

    if (riskType === FarmRiskType.Fo6Structures) {
      coverage = getIn(formRiskData, 'coverages.coverageE') || 0;
      coverageLabel = 'E';
    } else if (riskType === FarmRiskType.Fo6Scheduled) {
      // coverageF is an array of items with 'value' property - sum all values
      const coverageFArray = getIn(formRiskData, 'coverages.coverageF');
      coverage = Array.isArray(coverageFArray)
        ? coverageFArray.reduce((sum, item) => sum + (item?.value || 0), 0)
        : coverageFArray || 0;
      coverageLabel = 'F';
    } else if (riskType === FarmRiskType.Fo6Unscheduled) {
      coverage = getIn(formRiskData, 'coverages.coverageG') || 0;
      coverageLabel = 'G';
    }

    // Calculate actual dollar amounts
    // Wind/Hail can be either percentage (< 1) or dollar amount (>= 1)
    const windHailDollarAmount = windHailValue < 1 ? (coverage ? windHailValue * coverage : null) : windHailValue;

    // All-Peril can be either a percentage (< 1) or dollar amount (>= 1)
    const allPerilDollarAmount =
      allPerilValue < 1 ? (coverage ? Math.round(coverage * allPerilValue) : 0) : allPerilValue;

    // If wind/hail is a percentage but coverage is missing, reject
    if (windHailValue < 1 && windHailDollarAmount === null) {
      return this.createError({
        message: `Coverage ${coverageLabel} is required to calculate percentage-based Wind/Hail deductible`
      });
    }

    // If all-peril is a percentage but coverage is missing, reject
    if (allPerilValue < 1 && allPerilDollarAmount === null) {
      return this.createError({
        message: `Coverage ${coverageLabel} is required to calculate percentage-based All-Peril deductible`
      });
    }

    // Round both sides to handle floating point precision issues
    const windHailRounded = windHailDollarAmount !== null ? Math.round(windHailDollarAmount) : null;
    const allPerilRounded = Math.round(allPerilDollarAmount);

    if (windHailRounded !== null && windHailRounded < allPerilRounded) {
      // Format display values for error message
      const windHailDisplay =
        windHailValue < 1
          ? `${(windHailValue * 100).toFixed(0)}% ($${windHailRounded.toLocaleString()})`
          : `$${windHailDollarAmount.toLocaleString()}`;

      const allPerilDisplay =
        allPerilValue < 1
          ? `${(allPerilValue * 100).toFixed(0)}% ($${allPerilRounded.toLocaleString()})`
          : `$${allPerilValue.toLocaleString()}`;

      return this.createError({
        message: `Wind/Hail deductible (${windHailDisplay}) cannot be less than All-Peril deductible (${allPerilDisplay}) for Coverage ${coverageLabel}`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateFO6WindHailDeductible', validateFO6WindHailDeductible);

/**
 * FO6 Structure Category Loss Settlement Validation
 *
 * This validation method enforces the business rules for structure category loss settlement
 * options at the schema level. It works in conjunction with the UI business logic in
 * FO6.utils.js to ensure data integrity.
 *
 * Validation Rules:
 * - Dwelling/Contents, Hoop Buildings, Fencing: Must be ACV only (roof: ACV or NONE with exclusion)
 * - Confinement, Poultry: Structure → ACV or RC; Roof → ACV, RC_ACV_WIND_HAIL, or NONE with exclusion
 * - Silos/Grain Bins: ACV or RC allowed (roof also allows NONE with exclusion; RC_ACV_WIND_HAIL is NOT valid)
 * - Barns/Outbuildings: Handled by existing building class validation
 *
 * @param {string} fieldName - Name of the field being validated (for error messages)
 * @param {string} message - Base validation error message
 * @returns {Yup.TestFunction} Yup test function
 */
export const validateStructureCategoryLossSettlement = function (fieldName, message) {
  return this.test(`${fieldName}-structure-category-validation`, message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const buildingType = getIn(formRiskData, 'building.buildingType');

    // Only validate for FO6 Structures
    if (riskType !== FarmRiskType.Fo6Structures) {
      return true;
    }

    // Structure category-specific validation
    switch (buildingType) {
      case StructureCategory.DwellingContents:
      case StructureCategory.HoopBuildings:
      case StructureCategory.Fencing: {
        // Must be ACV only (NONE allowed for roof loss settlement when roof exclusion is enabled)
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'NONE'] : ['ACV'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: `${fieldName} must be "Actual Cash Value" for this structure type`
          })
        );
      }

      case StructureCategory.ConfinementBuildings:
      case StructureCategory.PoultryBuildings: {
        // For structureLossSettlement: ACV or RC allowed (role restrictions handled in UI)
        // For roofLossSettlement: ACV or RC_ACV_WIND_HAIL only (plain RC is never valid for these types)
        // NONE allowed when roof exclusion is enabled
        // GitHub issue #29079
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'RC_ACV_WIND_HAIL', 'NONE'] : ['ACV', 'RC'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: isRoofField
              ? `${fieldName} must be a valid option for this structure type`
              : `${fieldName} must be "Actual Cash Value" or "Replacement Cost"`
          })
        );
      }

      case StructureCategory.SilosGrainBins: {
        // ACV or RC allowed for both structure and roof loss settlement (RC_ACV_WIND_HAIL is NOT valid for silos)
        // NONE allowed for roof loss settlement when roof exclusion is enabled
        // GitHub issue #29079
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'RC', 'NONE'] : ['ACV', 'RC'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: `${fieldName} must be "Actual Cash Value" or "Replacement Cost"`
          })
        );
      }

      case StructureCategory.BarnsOutbuildings:
        // Building class logic already handles this
        return true;

      default:
        return true;
    }
  });
};

// Register the method
Yup.addMethod(Yup.string, 'validateStructureCategoryLossSettlement', validateStructureCategoryLossSettlement);

const baseFarmValidationSchema = Yup.object().shape({
  farmCoverage: Yup.object().shape({
    policyLevelAdjustmentFactor: Yup.number()
      .transform((value, originalValue) => {
        // Convert empty strings to default value
        if (String(originalValue).trim() === '') {
          return 0;
        }
        // Convert NaN to default value
        if (Number.isNaN(value)) {
          return 0;
        }
        return value ?? 0;
      })
      .required()
      .min(-99, 'Adjustment factor must be between -99% and 99%')
      .max(99, 'Adjustment factor must be between -99% and 99%'),
    extraExpenseLimit: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        // Convert empty strings to null
        if (String(originalValue).trim() === '') {
          return null;
        }
        return value;
      })
      .test('min-when-provided', 'Farm Extra Expense Coverage minimum limit is $5,000', (value) => {
        // Skip validation if no coverage (null, undefined, or 0)
        if (!value) {
          return true;
        }
        return value >= 5000;
      })
      .test('max-when-provided', 'Farm Extra Expense Coverage maximum limit is $25,000', (value) => {
        // Skip validation if no coverage (null, undefined, or 0)
        if (!value) {
          return true;
        }
        return value <= 25000;
      })
  }),
  entity: Yup.object()
    .shape({
      id: Yup.string().nullable(),
      type: Yup.string()
        .nullable()
        .required('Add details about the entity requesting this offer')
        .test('joint-requires-coapplicant', 'A co-applicant is required when entity type is Joint', function (value) {
          // Only validate on Modify Offer (when offer.id exists)
          const offerId = this.options?.context?.offerId;

          // Skip validation for New Offer (no offer ID yet)
          if (!offerId) {
            return true;
          }

          // Only validate if entity type is "Joint"
          if (value === 'Joint') {
            // Get drivers array from root form values
            const formValues = this.from?.[this.from.length - 1]?.value;
            const drivers = formValues?.drivers;

            // Require at least 2 drivers AND one with isCoApplicant === true
            if (!drivers || drivers.length < 2) {
              return false;
            }

            const hasCoApplicant = drivers.some((driver) => driver.isCoApplicant === true);
            return hasCoApplicant;
          }

          return true;
        }),
      name: Yup.string()
        .nullable()
        .test('required-for-non-individual-entity', 'Entity name is required', function (value) {
          const entityType = this.parent.type;
          if (entityType && entityType !== 'Individual' && entityType !== 'Joint') {
            return !!value;
          }
          return true;
        }),
      address: Yup.object()
        .shape({
          address: Yup.string().nullable(),
          address2: Yup.string().nullable(),
          city: Yup.string().nullable(),
          state: Yup.string().nullable(),
          zip: Yup.string().nullable()
        })
        .nullable()
    })
    .test('has-entity-type', 'Add details about the entity requesting this offer', (value) => {
      // Ensure entity with a type is provided
      return value?.type;
    }),
  farmLocations: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().nullable(),
      description: Yup.string().required('Description is required'),
      legalDescription: Yup.object().shape({
        format: Yup.string().required('Format is required'),
        section: Yup.string().nullable(),
        township: Yup.string().nullable(),
        townshipCoordinates: Yup.string().nullable(),
        range: Yup.string().nullable(),
        rangeCoordinates: Yup.string().nullable(),
        lot: Yup.string().nullable(),
        block: Yup.string().nullable()
      }),
      additionalLegalDescription: Yup.string().nullable(),
      locationDirections: Yup.string().nullable(),
      numberOfAcres: Yup.number().nullable(),
      address: Yup.object().shape({
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(),
        country: Yup.string().nullable(),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        zip: Yup.string().required('Zip code is required')
      }),
      fpc: Yup.string().nullable(),
      fpcFromLocation: Yup.string().nullable(),
      fireHydrantWithin1000ft: Yup.boolean().nullable(),
      latitude: Yup.number().nullifyIfNaN().nullable(),
      longitude: Yup.number().nullifyIfNaN().nullable(),
      risks: Yup.array().of(
        Yup.object().shape({
          type: Yup.string().required('Risk type is required'),
          description: Yup.string().required('Description is required'),
          adjustmentFactor: Yup.number()
            .transform((value, originalValue) => {
              // Convert empty strings to default value
              if (String(originalValue).trim() === '') {
                return 0;
              }
              // Convert NaN to default value
              if (Number.isNaN(value)) {
                return 0;
              }
              return value ?? 0;
            })
            .required()
            .min(-99, 'Adjustment factor must be between -99% and 99%')
            .max(99, 'Adjustment factor must be between -99% and 99%'),
          adjustmentFactorTotal: Yup.number()
            .nullable()
            .min(-99, 'Adjustment factor total must be between -99% and 99%')
            .max(99, 'Adjustment factor total must be between -99% and 99%'),
          building: Yup.object().shape({
            basementSqFt: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Basement sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            basementType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Basement/Foundation is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            constructionType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Construction type is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryBuildings
                    ]
                  },
                  {
                    risk: FarmRiskType.Fo3,
                    requireIfRatedOnly: true,
                    excludedPropertyTypes: ({ state }) =>
                      getConstructionTypeExemptPropertyTypes(state, FarmRiskType.Fo3)
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Construction type is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            mobileHomeQualityGrade: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }]
              }),
            mobileHomeConfig: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Mobile home config is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }]
              })
              .invalidMobileHomeConfigForFo3(
                'Single-wide and double-wide mobile homes are not eligible for FO-3. Please select FO-1 or FO-2.'
              ),
            exteriorWallType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Exterior wall finish is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            garageCarCapacity: Yup.number()
              .nullable()
              .requiredForRisks({
                message: "Garage's car space is required",
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            garageType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Garage type is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            dwellingLocation: Yup.object().shape({
              address: Yup.string().nullable(),
              address2: Yup.string().nullable(),
              country: Yup.string().nullable(),
              city: Yup.string().nullable(),
              state: Yup.string().nullable(),
              zip: Yup.string().nullable()
            }),
            homeQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFamiliesInDwelling: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Families in dwelling is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFireplaces: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Fireplaces is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFullBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Full bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numHalfBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Half bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numStories: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Stories is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numWoodBurningStoves: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Wood stoves is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            ownershipStatus: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Ownership status is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            purchaseDate: Yup.string().nullable(),
            purchaseAmount: Yup.number().nullable(),
            roofOriginal: Yup.boolean().nullable(),
            roofShape: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof shape is required!',
                risks: [{ risk: FarmRiskType.Fo3, requireIfRatedOnly: true }]
              })
              .test('required-for-fo3-barndominium', 'Roof shape is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            roofDeck: Yup.string().nullable(),
            roofCover: Yup.string().nullable(),
            roofDeckAttachment: Yup.string().nullable(),
            roofToWallConnection: Yup.string().nullable(),
            openingProtection: Yup.string().nullable(),
            roofSWR: Yup.string().nullable(),
            roofType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof type is required',
                risks: [
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo4, requiredForPropertyTypes: ['MH'] }
                ]
              })
              .test('required-for-fo3-barndominium', 'Roof type is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            roofYear: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Roof year is required',
                risks: [{ risk: FarmRiskType.Fo3, requireIfRatedOnly: true }]
              })
              .test('required-for-fo3-barndominium', 'Roof year is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null;
                }
                return true;
              })
              .requiredIfFO6DwellingContents('Roof year is required')
              .test(
                'roof-year-after-year-built',
                'Roof year must be same as or later than year built',
                function (value) {
                  const yearBuilt = getIn(this.parent, 'yearBuilt');

                  // Skip validation if either value is missing
                  if (!value || !yearBuilt) {
                    return true;
                  }

                  const yearBuiltNum = parseInt(yearBuilt, 10);
                  const roofYearNum = parseInt(value, 10);

                  if (roofYearNum < yearBuiltNum) {
                    return this.createError({
                      message: `Roof year (${roofYearNum}) cannot be earlier than building year (${yearBuiltNum})`
                    });
                  }

                  return true;
                }
              ),
            roofYearChangedManually: Yup.boolean().nullable(),
            sqFt: Yup.number()
              .nullable()
              .requiredForNonTowerSilos('Square footage is required')
              .requiredForRisks({
                message: 'Square footage is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true }
                ]
              })
              .test('required-for-fo3-barndominium', 'Square footage is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null;
                }
                return true;
              })
              .min(150, 'Square footage must be greater than 150 square feet')
              .mobileHomeSqFtValidation(),
            originalSqFt: Yup.number().nullable(),
            swimmingPool: Yup.object()
              .nullable()
              .default(undefined)
              .shape({
                typeOfPool: Yup.string()
                  .nullable()
                  .test('required-swimming-pool-type', 'Type of pool is required', function (value) {
                    // Swimming pool type is intentionally required for FO1, FO2, FO3, FO4 risk types.
                    // This ensures users must explicitly select a pool type (or "None") before finalizing.
                    // Check if this risk type requires swimming pool type
                    if (requiresSwimmingPoolType(this.options)) {
                      return !!value;
                    }
                    return true;
                  }),
                hasRemovableStepsOrLockingGate: Yup.boolean()
                  .nullable()
                  .test(
                    'required-for-dwelling-above-ground',
                    'This field is required for above-ground pools',
                    function (value) {
                      const typeOfPool = getIn(this.parent, 'typeOfPool');
                      if (isDwellingOrRentersRisk(this.options) && typeOfPool === 'A') {
                        return value !== null && value !== undefined;
                      }
                      return true;
                    }
                  ),
                has4FtHighFenceWithSelfLatchingGate: Yup.boolean()
                  .nullable()
                  .test(
                    'required-for-dwelling-in-ground',
                    'This field is required for in-ground pools',
                    function (value) {
                      const typeOfPool = getIn(this.parent, 'typeOfPool');
                      if (isDwellingOrRentersRisk(this.options) && typeOfPool === 'I') {
                        return value !== null && value !== undefined;
                      }
                      return true;
                    }
                  ),
                hasDivingBoardOrPoolSlide: Yup.boolean()
                  .nullable()
                  .test('required-for-dwelling-with-pool', 'This field is required', function (value) {
                    const typeOfPool = getIn(this.parent, 'typeOfPool');
                    if (isDwellingOrRentersRisk(this.options) && (typeOfPool === 'A' || typeOfPool === 'I')) {
                      return value !== null && value !== undefined;
                    }
                    return true;
                  }),
                limit: Yup.number().nullable()
              }),
            typeOfHome: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home type is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            occupancy: Yup.string()
              .nullable()
              .requiredIfFO6BarnsOutbuildings('Occupancy is required')
              .requiredIfFO6SilosGrainBins('Occupancy is required')
              .requiredIfFO6ConfinementBuildings('Occupancy is required'),
            residenceType: Yup.string().nullable().requiredIfFO6DwellingContents('Residence type is required'),
            yearBuilt: Yup.string()
              .nullable()
              .validYearBuilt()
              .requiredForRisks({
                message: 'Year built is required',
                risks: [{ risk: FarmRiskType.Fo3, requireIfRatedOnly: true }]
              })
              .test('required-for-fo3-barndominium', 'Year built is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null && String(value).trim() !== '';
                }
                return true;
              })
              .requiredForFO6Structures('Year built is required')
              .test(
                'year-built-building-class-1-compatibility',
                'Building age incompatible with Building Class 1 requirements',
                function (value) {
                  // Access sibling fields within the building object
                  const hasOpenSides = getIn(this.parent, 'structureOpenSides');
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Skip risk type check for manual validation - rely on field conditions only
                  const riskType = FarmRiskType.Fo6Structures; // Assume FO6 since this is in FO6 validation

                  // Only validate for FO6 Barns/Outbuildings with Building Class 1
                  if (
                    buildingClass !== 1 ||
                    riskType !== FarmRiskType.Fo6Structures ||
                    buildingType !== StructureCategory.BarnsOutbuildings
                  ) {
                    return true;
                  }

                  const currentYear = new Date().getFullYear();
                  const yearBuilt = parseInt(value, 10);
                  const buildingAge = yearBuilt ? currentYear - yearBuilt : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const formRiskData = this.from?.[1]?.value;
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // Building Class 1 requires age ≤ maxAge years AND no open sides
                  const shouldFail = hasOpenSides || (buildingAge !== null && buildingAge > maxAge);

                  if (shouldFail) {
                    return false;
                  }

                  return true;
                }
              )
              .test(
                'year-built-before-roof-year',
                'Building year must be same as or earlier than roof year',
                function (value) {
                  const roofYear = getIn(this.parent, 'roofYear');

                  // Skip validation if either value is missing
                  if (!value || !roofYear) {
                    return true;
                  }

                  const yearBuiltNum = parseInt(value, 10);
                  const roofYearNum = parseInt(roofYear, 10);

                  if (yearBuiltNum > roofYearNum) {
                    return this.createError({
                      message: `Building year (${yearBuiltNum}) cannot be later than roof year (${roofYearNum})`
                    });
                  }

                  return true;
                }
              ),
            latitude: Yup.number().nullifyIfNaN().nullable(),
            longitude: Yup.number().nullifyIfNaN().nullable(),
            stormShutters: Yup.boolean().nullable(),
            porchSqFt: Yup.number().nullable(),
            porchCode: Yup.string().nullable(),
            constructionStyle: Yup.string().nullable(),
            areasUnderConstruction: Yup.boolean()
              .nullable()
              .test('renovation-fields-required', 'Renovation details are incomplete', function (value) {
                if (!value) {
                  return true;
                }
                const renovation = this.parent?.renovation;
                const renovationFields = [
                  'electricalType',
                  'electricalService',
                  'heatingType',
                  'heatingYear',
                  'coolingYear',
                  'plumbingYear',
                  'electricalYear'
                ];
                const hasMissing = renovationFields.some((field) => !renovation?.[field]);
                if (!hasMissing) {
                  return true;
                }
                const riskDescription = this.from?.[1]?.value?.description || 'this risk';
                return this.createError({
                  message: `You've indicated ${riskDescription} has renovations, please provide those details or uncheck the box to continue`
                });
              }),
            hasTrampoline: Yup.boolean().nullable(),
            // Mobile home specific fields
            tieDownType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Tie down type is required',
                risks: [
                  { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] },
                  { risk: FarmRiskType.Fo4, requiredForPropertyTypes: ['MH'] }
                ]
              }),
            enclosedMasonryFoundation: Yup.boolean().nullable(),
            fullSkirtingAroundBase: Yup.boolean().nullable(),
            // Fields for both Barndominium and MobileHome
            renovation: Yup.object()
              .shape({
                electricalType: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical type is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                electricalService: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical service is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                heatingType: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Heating type is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                heatingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Heating year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                coolingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'AC year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                plumbingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Plumbing year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                electricalYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  })
              })
              .nullable(),
            attachedStructureSqFt: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Attached garage sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            // Well pump - all fields required if any is filled
            numWellPumps: Yup.number()
              .nullable()
              .test('well-pump-required', 'Number of pumps is required', function (value) {
                const { wellPumpEstimatedValue, wellPumpDescription } = this.parent;
                const hasEstValue = wellPumpEstimatedValue != null && wellPumpEstimatedValue > 0;
                const hasDescription = wellPumpDescription != null && wellPumpDescription.trim() !== '';

                if (hasEstValue || hasDescription) {
                  return value != null && value > 0;
                }
                return true;
              })
              .test('well-pump-min', 'Number of pumps must be at least 1', (value) => {
                if (value != null) {
                  return value >= 1;
                }
                return true;
              }),
            wellPumpEstimatedValue: Yup.number()
              .nullable()
              .test('well-pump-value-required', 'Estimated value is required', function (value) {
                const { numWellPumps, wellPumpDescription } = this.parent;
                const hasNumPumps = numWellPumps != null && numWellPumps > 0;
                const hasDescription = wellPumpDescription != null && wellPumpDescription.trim() !== '';

                if (hasNumPumps || hasDescription) {
                  return value != null && value > 0;
                }
                return true;
              }),
            wellPumpDescription: Yup.string()
              .nullable()
              .test('well-pump-description-required', 'Description of pumps or motors is required', function (value) {
                const { numWellPumps, wellPumpEstimatedValue } = this.parent;
                const hasNumPumps = numWellPumps != null && numWellPumps > 0;
                const hasEstValue = wellPumpEstimatedValue != null && wellPumpEstimatedValue > 0;

                if (hasNumPumps || hasEstValue) {
                  return value != null && value.trim() !== '';
                }
                return true;
              }),
            numSolidFuelBurningAppliances: Yup.number()
              .nullable()
              .min(0, 'Number of appliances cannot be negative')
              .requiredForRisks({
                message: 'Solid fuel burning appliances count is required',
                risks: [
                  { risk: FarmRiskType.Fo1 },
                  { risk: FarmRiskType.Fo2 },
                  { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] },
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryBuildings
                    ]
                  },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [StructureCategory.DwellingContents]
                  }
                ]
              }),
            primaryHeatingSource: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Primary heating source is required',
                risks: [
                  { risk: FarmRiskType.Fo1, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo2, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Primary heating source is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            secondaryHeatingSource: Yup.string().nullable(),
            buildingQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Quality is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings
                    ]
                  }
                ]
              }),
            buildingClass: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Building class is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [StructureCategory.BarnsOutbuildings]
                  }
                ]
              })
              .test(
                'building-class-1-age-eligibility',
                'Building Class 1 requires eligible building age and no open sides',
                function (value) {
                  // Only validate for FO6 Barns/Outbuildings with Building Class 1
                  const buildingType = getIn(this.parent, 'buildingType');
                  const riskType = FarmRiskType.Fo6Structures; // Assume FO6 since this is in FO6 validation

                  if (
                    riskType !== FarmRiskType.Fo6Structures ||
                    buildingType !== StructureCategory.BarnsOutbuildings ||
                    value !== 1
                  ) {
                    return true;
                  }

                  const yearBuilt = getIn(this.parent, 'yearBuilt');
                  const hasOpenSides = getIn(this.parent, 'structureOpenSides');

                  const currentYear = new Date().getFullYear();
                  const buildingAge = yearBuilt ? currentYear - parseInt(yearBuilt, 10) : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const formRiskData = this.from?.[1]?.value;
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // Class 1 requires age ≤ maxAge years AND no open sides
                  if (buildingAge !== null && buildingAge > maxAge) {
                    return this.createError({
                      message: `Building Class 1 requires building age ≤ ${maxAge} years. Current building age: ${buildingAge} years.`
                    });
                  }

                  if (hasOpenSides) {
                    return this.createError({
                      message: 'Building Class 1 cannot have open sides.'
                    });
                  }
                  return true;
                }
              )
              .test(
                'building-class-2-coverage-requirements',
                'Building Class 2 requires specific coverage configuration',
                function (value) {
                  // Only validate for FO6 Barns/Outbuildings with Building Class 2
                  const buildingType = getIn(this.parent, 'buildingType');
                  const formRiskData = this.from?.[1]?.value;
                  if (!formRiskData) {
                    return true; // Skip validation if form context unavailable
                  }

                  // Skip coverage validation for new risks (risks without an id)
                  if (isNewRisk(formRiskData)) {
                    return true;
                  }

                  const riskType = getIn(formRiskData, 'type');

                  if (
                    riskType !== FarmRiskType.Fo6Structures ||
                    buildingType !== StructureCategory.BarnsOutbuildings ||
                    value !== 2
                  ) {
                    return true;
                  }

                  // Get current coverage values
                  const coverages = getIn(formRiskData, 'coverages');
                  const structureLossSettlement = coverages?.structureLossSettlement;
                  const specialFormCoverage = coverages?.specialFormCoverage;
                  const hasSnowWeightCoverage = coverages?.hasSnowWeightCoverage;

                  // Class 2 requires RC + Special Form + Snow/Ice coverage
                  const errors = [];

                  if (structureLossSettlement !== 'RC') {
                    errors.push('Replacement Cost loss settlement');
                  }

                  if (specialFormCoverage !== true) {
                    errors.push('Special Form coverage');
                  }

                  if (hasSnowWeightCoverage !== true) {
                    errors.push('Weight of Ice, Snow, or Sleet coverage');
                  }

                  if (errors.length > 0) {
                    return this.createError({
                      message: `Building Class 2 requires: ${errors.join(', ')}.`
                    });
                  }

                  return true;
                }
              ),
            structureOpenSides: Yup.boolean()
              .nullable()
              .test('no-open-sides-for-class-1', 'Class 1 structures cannot have open sides', function (value) {
                const buildingClass = getIn(this.parent, 'buildingClass');
                return !(buildingClass === 1 && value === true);
              })
              .test(
                'no-open-sides-for-barn-type-1',
                'Buildings of this age cannot have open sides (violates Type 1 requirements)',
                function (value) {
                  // Access sibling fields within the building object
                  const yearBuilt = getIn(this.parent, 'yearBuilt');
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Get risk type from parent context (navigate up to risk level)
                  const formRiskData = this.from?.[1]?.value;
                  if (!formRiskData) {
                    return true; // Skip validation if form context unavailable
                  }

                  const riskType = getIn(formRiskData, 'type');

                  // Only validate for FO6 Barns/Outbuildings with Class 1
                  // Class 2 and 3 are allowed to have open sides regardless of age
                  if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
                    return true;
                  }

                  if (buildingClass !== 1) {
                    return true;
                  }

                  const currentYear = new Date().getFullYear();
                  const buildingAge = yearBuilt ? currentYear - parseInt(yearBuilt, 10) : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // If building age <= maxAge, it would be Type 1, so cannot have open sides
                  if (buildingAge !== null && buildingAge <= maxAge && value === true) {
                    return this.createError({
                      message: `Buildings ${maxAge} years old or newer cannot have open sides (violates Type 1 requirements)`
                    });
                  }

                  return true;
                }
              )
              .test(
                'open-sides-type-1-eligibility',
                'Type 1 barns cannot have open sides. Remove Type 1 coverages or fix building characteristics.',
                function (value) {
                  // Access sibling fields within the building object
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Get risk type and coverage data
                  const formRiskData = this.from?.[1]?.value;
                  if (!formRiskData) {
                    return true; // Skip validation if form context unavailable
                  }
                  const riskType = getIn(formRiskData, 'type');
                  const coverages = getIn(formRiskData, 'coverages');

                  // Only validate for FO6 Barns/Outbuildings
                  if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
                    return true;
                  }

                  // Class 2 and 3 are allowed to have open sides even with the same coverages
                  if (buildingClass !== 1) {
                    return true;
                  }

                  // Check if this appears to be configured as Type 1 (has Type 1 coverages)
                  const hasType1Coverages =
                    coverages?.structureLossSettlement === 'RC' &&
                    coverages?.specialFormCoverage === true &&
                    coverages?.hasSnowWeightCoverage === true;

                  // If configured with Type 1 coverages but has open sides, block
                  if (hasType1Coverages && value === true) {
                    return this.createError({
                      message: 'Building configured as Type 1 cannot have open sides.'
                    });
                  }

                  return true;
                }
              ),
            numHeatingSources: Yup.number().nullable(),
            numSecondaryHeatingSources: Yup.number().nullable(),
            diameter: Yup.string().nullable().requiredForTowers('Diameter is required for towers'),
            height: Yup.string().nullable().requiredForTowers('Height is required for towers'),
            bushelCapacity: Yup.string().nullable().requiredForGrainStorage('Bushel capacity is required'),
            containsInGroundPool: Yup.boolean().nullable(),
            residenceUsage: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Residence usage is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }],
                requireIfRatedOnly: true
              }),
            propertyType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Structure category is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }]
              })
          }),
          coverages: Yup.object().shape({
            coverageA: Yup.number()
              .coverageInRangeRequiredWhenEditable([FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3])
              .nullable()
              .rc80PercentRequirement({ validRiskTypes: [FarmRiskType.Fo3] }),
            coverageB: Yup.number().nullable(),
            coverageC: Yup.number().nullable().validateDwellingCoverageC('Invalid Coverage C value'),
            coverageD: Yup.number().nullable().validateFO1CoverageD('Invalid Coverage D value'),
            coverageE: Yup.number()
              .coverageInRangeRequiredWhenEditable([
                FarmRiskType.Fo6Unscheduled,
                FarmRiskType.Fo6Scheduled,
                FarmRiskType.Fo6Structures
              ])
              .nullable()
              .rc80PercentRequirement({ validRiskTypes: [FarmRiskType.Fo6Structures] })
              .requiredIfFO6DwellingContents('Coverage E - Dwelling Limit is required'),
            coverageF: Yup.array()
              .of(
                Yup.object().shape({
                  id: Yup.string().nullable(),
                  category: Yup.string().nullable(),
                  limit: Yup.number().nullable(),
                  description: Yup.string().nullable(),
                  deductible: Yup.number().nullable(),
                  deductibleWindHail: Yup.number().nullable(),
                  additionalPerils: Yup.number().nullable(),
                  multipleLossCoverage: Yup.boolean().nullable(),
                  livestockCategory: Yup.string().nullable(),
                  hasOpenPerils: Yup.boolean().nullable(),
                  hasReplacementCostCoverage: Yup.boolean().nullable(),
                  serialNumber: Yup.string().nullable(),
                  make: Yup.string().nullable(),
                  increaseStackLimit: Yup.number().nullable(),
                  hasWinterPerils: Yup.boolean().nullable(),
                  hasSpecialCattleEndorsement: Yup.boolean().nullable(),
                  hasSuffocationOfLivestock: Yup.boolean().nullable()
                })
              )
              .nullable()
              .requiredForRisks({
                message: 'At least one coverageF item is required',
                risks: [{ risk: FarmRiskType.Fo6Scheduled, requireIfRatedOnly: true }]
              }),
            coverageG: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Blanket limit is required',
                risks: [{ risk: FarmRiskType.Fo6Unscheduled }]
              }),
            coverageMS: Yup.boolean().nullable(),
            hasActiveCoverageMS: Yup.boolean().nullable(),
            deductible: Yup.number().nullable(),
            deductibleAllOther: Yup.number().nullable(),
            deductibleWindHail: Yup.number()
              .nullable()
              .validateWindHailDeductible('Invalid Wind/Hail deductible')
              .validateFO6WindHailDeductible('Invalid Wind/Hail deductible for FO6'),
            deductibleEarthquake: Yup.number().nullable(),
            deductibleEarthquake2: Yup.number().nullable(),
            roofExclusion: Yup.string().nullable(),
            roofExclusionDetails: Yup.string().nullable(),
            exteriorWallSurfacingExclusion: Yup.boolean().nullable(),
            exteriorWallSurfacingExclusionDetails: Yup.string()
              .nullable()
              .when('exteriorWallSurfacingExclusion', {
                is: (value) => !!value,
                then: (schema) =>
                  schema.required('Description is required when exterior wall surfacing exclusion is enabled'),
                otherwise: (schema) => schema.nullable()
              }),
            otherStructuresExclusion: Yup.boolean().nullable(),
            otherStructuresExclusionDetails: Yup.string().nullable(),
            hasSnowWeightCoverage: Yup.boolean().nullable().barnType1Validation(),
            structureLossSettlement: Yup.string()
              .nullable()
              .validateStructureCategoryLossSettlement('Structure Loss Settlement')
              .barnType1Validation(),
            roofLossSettlement: Yup.string()
              .nullable()
              .validateStructureCategoryLossSettlement('Roof Loss Settlement')
              .test('roof-acv-constraint', 'Roof Loss Settlement cannot be RC when Structure is ACV', function (value) {
                const structureLoss = getIn(this.parent, 'structureLossSettlement');
                if (structureLoss === 'ACV' && value === 'RC') {
                  return this.createError({
                    message:
                      'Roof Loss Settlement cannot be "Replacement Cost" when Structure Loss Settlement is "Actual Cash Value"'
                  });
                }
                return true;
              }),
            specialFormCoverage: Yup.boolean().nullable().barnType1Validation(),
            windHailExclusion: Yup.boolean().nullable(),
            windHailExclusionDetails: Yup.string().nullable(),
            otherStructuresRoofExclusion: Yup.boolean().nullable(),
            sidingExclusion: Yup.boolean().nullable(),
            sidingExclusionDetails: Yup.string().nullable(),
            stormDamageExclusion: Yup.boolean().nullable(),
            stormDamageExclusionDetails: Yup.string().nullable(),
            scheduleCoverages: Yup.array()
              .of(
                Yup.object()
                  .shape({
                    coverageType: Yup.string().nullable(),
                    items: Yup.array()
                      .of(
                        Yup.object().shape({
                          id: Yup.string().nullable(),
                          category: Yup.string().nullable(),
                          limit: Yup.number().nullable(),
                          description: Yup.string().nullable(),
                          deductible: Yup.number().nullable(),
                          extendedDetails: Yup.object()
                            .shape({
                              name: Yup.string().nullable(),
                              year: Yup.number().nullable(),
                              horsepower: Yup.number().nullable(),
                              serialNumber: Yup.string().nullable(),
                              classification: Yup.string().nullable(),
                              dimensions: Yup.string().nullable(),
                              kindOfAnimal: Yup.string().nullable(),
                              sex: Yup.string().nullable(),
                              breed: Yup.string().nullable(),
                              age: Yup.number().nullable(),
                              originalCost: Yup.number().nullable(),
                              coinsurance: Yup.number().nullable(),
                              days: Yup.number().nullable(),
                              startDate: Yup.string().nullable(),
                              endDate: Yup.string().nullable()
                            })
                            .nullable()
                        })
                      )
                      .nullable()
                  })
                  .nullable()
              )
              .nullable(),
            coverageWaterBackup: Yup.number().nullable(),
            coverageISBC: Yup.number().nullable(),
            coverageRSExtended: Yup.boolean().nullable(),
            poolLimit: Yup.number().nullable(),
            resUnderConstructionLimit: Yup.number().nullable(),
            resUnderConstructionEffectiveDate: Yup.string().nullable(),
            inlandFloodInceptionDate: Yup.string().nullable(),
            inlandFloodOccurrenceLimit: Yup.number().nullable(),
            inlandFloodAggregateLimit: Yup.number().nullable(),
            inlandFloodDeductible: Yup.number().nullable(),
            inlandFloodDeductibleOverride: Yup.number().nullable(),
            inlandFloodRiskScore: Yup.number().nullable(),
            expandedDebrisRemoval: Yup.boolean().nullable(),
            serviceLine: Yup.boolean().nullable(),
            personalPropertyReplCost: Yup.boolean().nullable(),
            businessPropertyLimit: Yup.number().nullable(),
            businessPropertyLimitDetails: Yup.string().nullable(),
            moneyLimit: Yup.number().nullable(),
            jewelryLimit: Yup.number().nullable(),
            securitiesLimit: Yup.number().nullable(),
            motorizedVehiclesLimit: Yup.number().nullable(),
            silverwareLimit: Yup.number().nullable(),
            gunsLimit: Yup.number().nullable(),
            tenantImprovementLimit: Yup.number().nullable(),
            sprinklerLeakageBuildingLimit: Yup.number().nullable(),
            sprinklerLeakageLimitDetails: Yup.string()
              .nullable()
              .when('sprinklerLeakageBuildingLimit', {
                is: (value) => value != null && value > 0,
                then: Yup.string().required(
                  'Description of premise is required when sprinkler leakage limit is provided'
                ),
                otherwise: Yup.string().nullable()
              }),
            canEditCoverageAmount: Yup.boolean().nullable(),
            adjustDwellingLimitDescription: Yup.string()
              .requiredWhenCanEditCoverageAmount('Description of reason for adjusting dwelling limit is required')
              .nullable(),
            cosmeticDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Cosmetic damage exclusion is required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              })
              .test(
                'at-least-one-exclusion-type',
                'You must have at least one cosmetic damage surfacing type selected in order to exclude it.',
                function (value) {
                  if (!value) {
                    return true;
                  }

                  const { exteriorDoorsWindowsDamageExclusion, exteriorWallsDamageExclusion, roofsDamageExclusion } =
                    this.parent;

                  return exteriorDoorsWindowsDamageExclusion || exteriorWallsDamageExclusion || roofsDamageExclusion;
                }
              ),
            exteriorDoorsWindowsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              }),
            exteriorWallsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              }),
            roofsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              })
          }),
          connectedHome: Yup.object().shape({
            monitored: Yup.string().nullable(),
            moistureDevices: Yup.number().nullable(),
            theftPreventionDevices: Yup.number().nullable(),
            theftPreventionDeviceType: Yup.string().nullable(),
            motionDetectingDevices: Yup.number().nullable(),
            smokeDetectingDevices: Yup.number().nullable(),
            fireProtectiveDeviceType: Yup.string().nullable(),
            internetMonitoring: Yup.boolean().nullable(),
            autoWaterShutoffDevices: Yup.boolean().nullable(),
            providerName: Yup.string().nullable(),
            agreeToTerms: Yup.boolean().nullable()
          })
        })
      )
    })
  ),
  drivers: Yup.array()
    .of(
      Yup.object().shape({
        dateOfBirth: Yup.string()
          .nullable()
          .required('Date of birth is required')
          .test('minimum-driver-age', '', function (value) {
            if (value) {
              const driver = this.options?.context?.drivers?.[this.options?.index];
              const firstName = driver?.firstName ?? '';
              const lastName = driver?.lastName ?? '';
              const today = new Date();
              const birthDate = new Date(value);
              const age = differenceInYears(today, birthDate);

              if (age < 14) {
                return this.createError({
                  message: `${firstName} ${lastName} is too young to be on the policy`,
                  path: this.path
                });
              }
            }
            return true;
          }),
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required')
      })
    )
    .nullable()
});

export const getSharedFarmSchema = async () => {
  let schema = baseFarmValidationSchema;

  try {
    const brandValidationModule = await import(`./brand-schemas/${brandConstants.SHORT_NAME?.toLowerCase()}.js`);

    if (brandValidationModule.default) {
      schema = schema.concat(brandValidationModule.default);
    }
  } catch {
    // Brand validation schema not found - silently continue with base schema
  }

  return schema;
};
